import i18n from "i18n"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import InputAdornment from "@mui/material/InputAdornment"
import CloseIcon from "@mui/icons-material/Close"
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material"
import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Auth } from "aws-amplify"
import ModalConfirm from "./ModalConfirm"

const ModalPassword = ({ openPassword, setOpenPassword }) => {
  const [showActual, setShowActual] = useState(false)
  const [showNueva, setShowNueva] = useState(false)
  const [showRepita, setShowRepita] = useState(false)
  useEffect(() => {
    if (openPassword) {
      setContra({ actual: "", nueva: "", repita: "" })
      setErrors({ actual: "", nueva: "", repita: "" })
    }
  }, [openPassword])

  const [contra, setContra] = useState({ actual: "", nueva: "", repita: "" })
  const [errors, setErrors] = useState({ actual: "", nueva: "", repita: "" })
  const [openNewModal, setOpenNewModal] = useState(false)
  const validateFields = () => {
    let newErrors = { actual: "", nueva: "", repita: "" }
    // Validación actual
    if (!contra.actual) {
      newErrors.actual = i18n.t("profile.passwordIncorrect")
    }
    // Validación nueva
    const hasUppercase = /[A-Z]/.test(contra.nueva)
    const hasLowercase = /[a-z]/.test(contra.nueva)
    const hasNumber = /[0-9]/.test(contra.nueva)
    if (
      !contra.nueva ||
      contra.nueva.length < 8 ||
      !hasUppercase ||
      !hasLowercase ||
      !hasNumber
    ) {
      newErrors.nueva = i18n.t("profile.passwordRequirements")
    }
    // Validación repita
    if (contra.nueva !== contra.repita) {
      newErrors.repita = i18n.t("profile.passwordsNotMatch")
    }
    setErrors(newErrors)
    return Object.values(newErrors).every(e => e === "")
  }

  const handleChange = e => {
    setContra({ ...contra, [e.target.name]: e.target.value })
    setErrors({ ...errors, [e.target.name]: "" })
  }

  const changePassword = async () => {
    if (!validateFields()) return
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.changePassword(user, contra.actual, contra.nueva)
      setOpenNewModal(true)
      setOpenPassword(false)
    } catch (err) {
      console.error("Error al cambiar la contraseña:", err)
      let errorMsg = i18n.t("profile.passwordIncorrect")
      if (err && err.name === "LimitExceededException") {
        errorMsg = i18n.t("profile.passwordLimitExceeded")
      } else if (err && err.name === "NotAuthorizedException") {
        errorMsg = i18n.t("profile.passwordIncorrect")
      } else if (err && err.message) {
        errorMsg = err.message
      }
      setErrors({ ...errors, actual: errorMsg })
    }
  }
  return (
    <>
      <Dialog
        open={openPassword}
        onClose={() => setOpenPassword(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
            {i18n.t("profile.changePassword")}
          </Typography>
          <IconButton onClick={() => setOpenPassword(false)} sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" id="Form" sx={{ mt: 2 }}>
            {/* Campo oculto de username para accesibilidad y gestores de contraseñas */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              style={{ display: "none" }}
              tabIndex={-1}
            />
            <TextField
              type={showActual ? "text" : "password"}
              name="actual"
              label={i18n.t("profile.currentPassword")}
              onChange={handleChange}
              value={contra.actual}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              error={!!errors.actual}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={i18n.t("profile.togglePasswordVisibility")}
                      onClick={() => setShowActual(show => !show)}
                      edge="end"
                      size="small"
                    >
                      {showActual ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              type={showNueva ? "text" : "password"}
              name="nueva"
              label={i18n.t("profile.newPassword")}
              onChange={handleChange}
              value={contra.nueva}
              variant="outlined"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              error={!!errors.nueva}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={i18n.t("profile.togglePasswordVisibility")}
                      onClick={() => setShowNueva(show => !show)}
                      edge="end"
                      size="small"
                    >
                      {showNueva ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              type={showRepita ? "text" : "password"}
              name="repita"
              onChange={handleChange}
              value={contra.repita}
              variant="outlined"
              label={i18n.t("profile.confirmNewPassword")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              error={!!errors.repita}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={i18n.t("profile.togglePasswordVisibility")}
                      onClick={() => setShowRepita(show => !show)}
                      edge="end"
                      size="small"
                    >
                      {showRepita ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Mensaje de error general debajo de los campos y encima del botón */}
            {(errors.actual || errors.nueva || errors.repita) && (
              <Typography color="error" fontSize={14} mb={2}>
                {errors.actual || errors.nueva || errors.repita}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button
            variant="contained"
            color="branded"
            size="large"
            onClick={changePassword}
          >
            {i18n.t("profile.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ModalConfirm open={openNewModal} setOpen={setOpenNewModal} />
    </>
  )
}

ModalPassword.propTypes = {
  openPassword: PropTypes.bool.isRequired,
  setOpenPassword: PropTypes.func.isRequired,
}

export default ModalPassword
